import React, {
  CSSProperties, useContext,
} from 'react';
import { get as _get } from 'lodash';
import {
  Grid,
  Checkbox,
  Typography,
  FormControlLabel,
  useMediaQuery,
  Tooltip,
  GridSize,
} from '@material-ui/core';
import {
  Control, Controller, FieldValues, get, UnpackNestedValue, UseFormGetValues, UseFormSetValue, UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SelectContact } from 'components/Contact/SelectContact';
import { useParams } from 'react-router-dom';
import { Contact } from 'Types';
import theme from 'theme';
import { Announcement } from '@material-ui/icons';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import { Certificates, ContextAssignContact } from './CommercialMechanicalServicesPanel';
import { commercialMechanicalServicesLabels } from './CommercialServicesFields';

interface FullCheckboxHeaderProps {
  title?: string,
  readOnly: boolean,
  checked: number,
  unchecked: number,
  groups: FullCheckboxGroupDetail[],
  onSelectAllChange: (v: boolean) => void
}

const FullCheckboxHeader = ({
  title, readOnly, checked, unchecked, onSelectAllChange, groups,
}: FullCheckboxHeaderProps) => {
  const { t } = useTranslation();
  const tabletView = useMediaQuery(theme.breakpoints.down(1000));
  const subHeaderStyle: CSSProperties = { textAlign: 'center', whiteSpace: 'normal' };
  const headerStyle: CSSProperties = {
    ...subHeaderStyle, width: 150, fontWeight: 'bold', paddingTop: 16, whiteSpace: 'normal',
  };
  const cols = groups.reduce((prev, curr) => [...prev, ...curr.cols], [] as any[]);
  const { handleAssignContactError } = useContext(ContextAssignContact);
  const checkboxesCount = cols.length;

  return (
    <>
      <Grid item xs={12 - checkboxesCount * 2 as GridSize} md={12 - checkboxesCount as GridSize} style={{ paddingLeft: 0, textAlign: 'right' }}>
        {!readOnly
          ? (
            <FormControlLabel
              control={(
                <Checkbox
                  color="primary"
                  checked={unchecked === 0}
                  indeterminate={checked > 0 && unchecked > 0}
                  onChange={(e, v) => onSelectAllChange(v)}
                  onClick={(e: any) => {
                    handleAssignContactError('select_all', e.target.checked);
                  }}
                />
              )}
              labelPlacement={tabletView ? 'bottom' : 'end'}
              label={<Typography style={tabletView ? { fontSize: '0.8em', whiteSpace: 'nowrap', display: 'flex-inline' } : {}}>{t('Select All')}</Typography>}
            />
          ) : null}
      </Grid>
      {groups.map((g) => <Grid key={`label-${g.label}`} item xs={4} md={2} style={headerStyle}>{g.label}</Grid>)}
      <Grid item xs={12 - checkboxesCount * 2 as GridSize} md={12 - checkboxesCount as GridSize} style={{ fontWeight: 'bold' }}>{title || ''}</Grid>
      {cols.map((c) => <Grid key={`col-${c.path}`} item xs={2} md={1} style={tabletView ? { ...subHeaderStyle, fontSize: '0.8em' } : subHeaderStyle}>{c.label}</Grid>)}
    </>
  );
};

interface FullCheckboxProps<T extends FieldValues = FieldValues> {
  title: string,
  field: FullCheckBoxItem<T>,
  control: Control<T>,
  readOnly: boolean,
  checkBoxesParts: string[],
  values: UnpackNestedValue<T>,
}

function FullCheckbox<T extends FieldValues = FieldValues>({
  title, field, control, readOnly, checkBoxesParts, values,
}: FullCheckboxProps<T>) {
  const { t } = useTranslation();
  const permissions = usePermissionsSPP();
  const tabletView = useMediaQuery(theme.breakpoints.down(1000));
  const tdStyle: CSSProperties = { textAlign: 'center', whiteSpace: 'normal' };
  const titleStyle: CSSProperties = { paddingLeft: 8, whiteSpace: 'normal' };
  const { btNumber, stNumber } = useParams<{ btNumber: string, stNumber: string }>();
  const countryCode = (permissions.location?.customer_country || 'us').toLocaleLowerCase();

  const {
    certificateServiceTypes, certificateServiceTypesErrors, handleAssignContact, handleAssignContactError,
  } = useContext(ContextAssignContact);

  const fieldParts: string[] = field.key.toString().split('.');

  const isRowChecked = (): boolean => {
    if (!(fieldParts[1] in (certificateServiceTypes || []))) return false; // only concerned with the service types requiring a contact
    const checked = checkBoxesParts.some((part) => _get(values, `${field.key as string}.${part}`));
    return checked;
  };

  const renderSelectContact = !readOnly
    && isRowChecked()
    && (fieldParts[1] in (certificateServiceTypes || []))
    && !((certificateServiceTypes as Certificates)[fieldParts[1]]);

  const checkboxesCount = checkBoxesParts.length;

  return (
    <>
      <Grid item xs={12 - checkboxesCount * 2 as GridSize} md={12 - checkboxesCount as GridSize}>
        {tabletView && typeof field.notes === 'string' ? (
          <Tooltip placement="top" arrow title={field.notes}>
            <Typography style={{ ...titleStyle, paddingLeft: 0, fontSize: '0.9em' }}>
              {title}
              <Announcement style={{
                opacity: 0.5, paddingLeft: 4, width: 24, verticalAlign: 'middle',
              }}
              />
            </Typography>
          </Tooltip>
        ) : (
          <Typography style={tabletView ? { ...titleStyle, paddingLeft: 0, fontSize: '0.9em' } : titleStyle}>{title}</Typography>
        )}
        {countryCode !== 'ca' && (
        <>
          {!tabletView && <Typography style={{ paddingLeft: 8, fontSize: '0.8em', opacity: typeof field.notes === 'string' ? 0.5 : undefined }}>{field.notes || ''}</Typography>}
          {renderSelectContact && (
          <>
            <span style={{ paddingLeft: tabletView ? 0 : 8 }} />
            <SelectContact
              shipToNumber={stNumber}
              billToNumber={btNumber}
              countryCode={countryCode}
              certification_type="mechanical"
              certification_job={fieldParts[1]}
              onChange={(contact: Contact | null) => {
                if (contact) {
                  const key: string = `${fieldParts[0]}.${fieldParts[1]}`;
                  handleAssignContact(contact, fieldParts[1], get(commercialMechanicalServicesLabels, key as string));
                  handleAssignContactError(fieldParts[1], !!((isRowChecked() && (certificateServiceTypes as Certificates)[fieldParts[1]])));
                }
              }}
            />
              {(fieldParts[1] in (certificateServiceTypesErrors || [])) && (
              <Typography color="error" style={{ paddingLeft: tabletView ? 0 : 8, fontSize: '0.8em' }}>{t('Certificate is required')}</Typography>
              )}
          </>
          )}
        </>
        )}
      </Grid>
      {checkBoxesParts.map((part) => (
        <Grid key={`grid_${field.key as string}.${part}` as any} item xs={2} md={1} style={tdStyle}>
          {(!field.enabledPaths || field.enabledPaths.includes(part)) && (
            <Controller
              name={`${field.key as string}.${part}` as any}
              control={control}
              render={(props: any) => (
                <Checkbox
                  id={`${field.key as string}.${part}`}
                  checked={props.field.value || false}
                  onChange={(e: any) => !readOnly && props.field.onChange(e.target.checked)}
                  readOnly={readOnly}
                  color="primary"
                  onClick={(e: any) => {
                    if (!(fieldParts[1] in (certificateServiceTypes || []))) return; // only concerned with the service types requiring a contact
                    handleAssignContactError(fieldParts[1], (e.target.checked && !(certificateServiceTypes as Certificates)[fieldParts[1]]) ? true : undefined);
                  }}
                />
              )}
            />
          )}
        </Grid>
      ))}
    </>
  );
}

interface FullCheckboxGroupDetail {
  label: string,
  cols: { label: string, path: string }[];
}

interface FullCheckBoxItem<L> {
  key: keyof L,
  notes?: React.ReactNode,
  enabledPaths?: string[],
}

interface FullChecboxPanelProps<T extends FieldValues = FieldValues, L = any> {
  control: Control<T>,
  getValues: UseFormGetValues<T>,
  readOnly: boolean,
  items: FullCheckBoxItem<L>[],
  labels: L,
  title?: string,
  watch: UseFormWatch<T>,
  setValue: UseFormSetValue<T>,
  groups: FullCheckboxGroupDetail[],
  paddingLeft?: number,
  paddingRight?: number,
}

export function FullChecboxPanel<T extends FieldValues = FieldValues, L = any>({
  control, readOnly, items, title, getValues, watch, setValue, labels, groups, paddingLeft = 35, paddingRight = 24,
}: FullChecboxPanelProps<T, L>) {
  const tabletView = useMediaQuery(theme.breakpoints.down(800));
  const { t } = useTranslation();
  let checked: number = 0;
  let unchecked: number = 0;
  const values = getValues();
  const checkBoxesParts: string[] = [];
  const watchFields: string[] = [];
  groups.forEach((g) => g.cols.forEach((c) => checkBoxesParts.push(c.path)));
  items.forEach((field) => {
    checkBoxesParts.forEach((part) => {
      if (field.enabledPaths && !field.enabledPaths.includes(part)) return;
      watchFields.push(`${field.key as string}.${part}`);
      if (!get(values, `${field.key as string}.${part}`)) unchecked++;
      else checked++;
    });
  });
  watch(watchFields as any);

  return (
    <Grid container spacing={2} style={tabletView ? { paddingLeft: 0, paddingRight: 0 } : { paddingLeft, paddingRight }}>
      <FullCheckboxHeader
        title={title}
        readOnly={readOnly}
        checked={checked}
        unchecked={unchecked}
        groups={groups}
        onSelectAllChange={(v) => {
          items.forEach((field) => {
            checkBoxesParts.forEach((part) => {
              if (field.enabledPaths && !field.enabledPaths.includes(part)) return;
              setValue(`${field.key as string}.${part}` as any, v as any, { shouldDirty: true });
            });
          });
        }}
      />
      {items.map((field) => (
        <FullCheckbox
          key={field.key as string}
          control={control}
          readOnly={readOnly}
          title={t(get(labels, field.key as string))}
          field={field as any}
          checkBoxesParts={checkBoxesParts}
          values={getValues()}
        />
      ))}
    </Grid>
  );
}
